import React from "react";
import { VictoryContainer, VictoryLabel, VictoryPie } from "victory";
import tailwindExt from "styles/tailwindExtensions";
import { capitalize } from "@tomocredit/common-fe/utils";
import creditScoreBlurred from "assets/credit-score-blur.png";

const tailwindExtensions = tailwindExt.tailwindExtensions;

const GAUGE_COLORS = [
  tailwindExtensions.colors["green-primary"],
  "#E5E5E5",
];

const DEFAULT_FONT_FAMILY = "Gilroy, sans-serif";

const MIN_CREDIT_SCORE = 300;
const MIN_CREDIT_SCORE_LABEL = String(MIN_CREDIT_SCORE);
const MAX_CREDIT_SCORE = 850;
const MAX_CREDIT_SCORE_LABEL = String(MAX_CREDIT_SCORE);
const CHART_WIDTH = 350;
const CHART_HEIGHT = 220;
const MODEL_LABEL_Y = {
  bottom: CHART_HEIGHT - 30,
  center: 135,
};

/**
 * @param {Object} props
 * @param {Object} props.creditScore
 * @param {boolean} props.isBlurred
 * @param {string} props.modelLabelPlacement - placement of the model label (center, bottom). Default is bottom
 * @returns
 */
const CreditScoreChart = (props) => {
  const { creditScore, isBlurred, modelLabelPlacement = "bottom" } = props;
  const latestCreditScore = creditScore;

  const currentScore = latestCreditScore?.credit_score || 780; // default to 780 if no score is available

  const currentScoreLabel = String(currentScore);
  const model = capitalize(latestCreditScore?.credit_scoring_model || "vantage 3.0");
  const modelLabel = model ? `Powered by ${model}` : "";

  const gaugeData = [
    {
      x: "score",
      y: currentScore - MIN_CREDIT_SCORE,
      label: MIN_CREDIT_SCORE_LABEL,
    },
    {
      x: "score_remainder",
      y: MAX_CREDIT_SCORE - currentScore,
      label: MAX_CREDIT_SCORE_LABEL,
    },
  ];

  return (
    <div className="relative">
      <VictoryContainer width={CHART_WIDTH} height={CHART_HEIGHT} responsive={false}>
        <VictoryPie
          width={CHART_WIDTH}
          height={CHART_HEIGHT}
          colorScale={GAUGE_COLORS}
          standalone={false}
          startAngle={-110}
          endAngle={110}
          data={gaugeData}
          innerRadius={90}
          labelPosition={({ index }) => (index === 0 ? "startAngle" : "endAngle")} // only update the first label -> score min/max
          labelComponent={
            <VictoryLabel
              textAnchor="middle"
              dy={25}
              style={{ fontSize: 16, fontFamily: DEFAULT_FONT_FAMILY, fontWeight: 700 }}
            />}
          labelRadius={100}
          padding={0}
          style={{
            labels: { fontSize: 16 },
            parent: { width: CHART_WIDTH, height: CHART_HEIGHT, scale: 2 },
          }}
        />
        <VictoryLabel
          textAnchor="middle"
          style={{ fontSize: 12, fontFamily: DEFAULT_FONT_FAMILY, fontWeight: 600 }}
          x={175}
          y={MODEL_LABEL_Y[modelLabelPlacement]}
          text={modelLabel}
        />
        {!isBlurred && (
          <VictoryLabel
            textAnchor="middle"
            className="text-4xl font-bold"
            style={{
              fontSize: 36,
              fontWeight: "700",
              fontFamily: DEFAULT_FONT_FAMILY,
            }}
            x={CHART_WIDTH / 2}
            y={CHART_HEIGHT / 2 - 15}
            text={currentScoreLabel}
          />
        )}
      </VictoryContainer>
      {isBlurred && (
        <img
          src={creditScoreBlurred}
          className="absolute"
          alt="Credit Score"
          style={{
            maxWidth: 60,
            height: 44,
            top: CHART_HEIGHT / 2 - 33,
            left: CHART_WIDTH / 2 - 30,
          }}
        />
      )}
    </div>
  );
};

export default CreditScoreChart;
